import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce'; 
import './Uploadbaru.css'; 

export const UploadFoto = () => {
  const [questionnaires, setQuestionnaires] = useState([]);
  const [selectedQuestionnaire, setSelectedQuestionnaire] = useState('');
  const [foto, setPhoto] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [nomorKTPTerm, setNomorKTPTerm] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileName, setFileName] = useState(''); // State untuk nama file
  const fileInputRef = useRef(null); // Ref untuk input file
  const videoRef = useRef(null);
  const navigate = useNavigate();

  // Create a debounced search function
  const debouncedSearch = useCallback(
    debounce(async (name, ktp) => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_URL}/getquestionnaires`, {
          params: {
            searchName: name,
            searchKTP: ktp,
            limit: 100 // Limit results but get enough to be useful
          },
          withCredentials: true,
        });
        setQuestionnaires(response.data.data || []);
      } catch (error) {
        console.error('Error fetching questionnaire data', error);
        setQuestionnaires([]);
      } finally {
        setIsLoading(false);
      }
    }, 500), // 500ms delay to reduce API calls
    []
  );

  // Effect to trigger search when terms change
  useEffect(() => {
    debouncedSearch(searchTerm, nomorKTPTerm);
  }, [searchTerm, nomorKTPTerm, debouncedSearch]);

  const startPreview = async () => {
    try {
      setIsPreviewing(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (error) {
      console.error('Error accessing camera:', error);
      alert('Tidak dapat mengakses kamera. Silakan periksa izin kamera Anda.');
      setIsPreviewing(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current) return;
    
    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    canvas.getContext('2d').drawImage(videoRef.current, 0, 0);

    const dataUrl = canvas.toDataURL('image/png');
    const blob = await fetch(dataUrl).then((res) => res.blob());
    const capturedFile = new File([blob], 'captured-photo.png', { type: 'image/png' });
    
    setPhoto(capturedFile);
    setFileName(capturedFile.name); // Set nama file
    setImagePreview(dataUrl);

    if (videoRef.current.srcObject) {
      videoRef.current.srcObject.getTracks().forEach((track) => track.stop());
    }
    setIsPreviewing(false);
  };

  const cancelPreview = () => {
    setImagePreview(null);
    setPhoto(null);
    setFileName(''); // Reset nama file
    if (fileInputRef.current) {
      fileInputRef.current.value = ''; // Reset input file
    }
    if (isPreviewing && videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject;
      stream.getTracks().forEach((track) => track.stop());
      setIsPreviewing(false);
    }
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name); // Set nama file yang dipilih
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
        setPhoto(file);
      };
      reader.readAsDataURL(file);
    } else {
      // Jika tidak ada file yang dipilih, reset semua
      setFileName('');
      setImagePreview(null);
      setPhoto(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedQuestionnaire || !foto) {
      alert('Pilih data dan foto terlebih dahulu!');
      return;
    }

    const formData = new FormData();
    formData.append('questionnaireId', selectedQuestionnaire);
    formData.append('foto', foto);

    try {
      const response = await axios.post(`${process.env.REACT_APP_URL}/uploadfoto`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      alert(response.data.message);
      navigate('/recap');
    } catch (error) {
      console.error('Error uploading photo:', error);
      if (error.response && error.response.data && error.response.data.message) {
        alert(error.response.data.message);
      } else {
        alert('Terjadi kesalahan saat mengunggah foto. Silakan coba lagi.');
      }
    }
  };

  return (
    <div className="upload-container">
      <div className="upload-header">
        <h2>Upload Foto</h2>
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="searchName">Cari Nama:</label>
        <input
          type="text"
          id="searchName"
          placeholder="Cari nama lengkap"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="form-control"
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="searchKTP">Cari Nomor KTP:</label>
        <input
          type="number"
          id="searchKTP"
          placeholder="Cari nomor KTP"
          value={nomorKTPTerm}
          onChange={(e) => setNomorKTPTerm(e.target.value)}
          className="form-control"
        />
      </div>

      <div className="form-group">
        <label className="form-label" htmlFor="questionnaire">Pilih Nama:</label>
        <select
          id="questionnaire"
          value={selectedQuestionnaire}
          onChange={(e) => setSelectedQuestionnaire(e.target.value)}
          className="form-select"
          disabled={isLoading}
        >
          <option value="">Pilih Nama</option>
          {questionnaires.map((q) => (
            <option key={q.id} value={q.id}>
              {q.namaLengkapKK} - {q.nomorKTP || 'No KTP tidak tersedia'}
            </option>
          ))}
        </select>
        {/* {isLoading && <small className="text-muted">Memuat data...</small>} */}
      </div>

      <div className="form-group">
        <label className="form-label">Upload Foto:</label>
        <div className="file-input-wrapper">
          <input 
            ref={fileInputRef}
            type="file" 
            accept="image/*" 
            onChange={handlePhotoChange} 
            className="form-control custom-file-input"
          />
          {/* Tampilkan nama file jika ada, atau placeholder jika tidak */}
          <div className="file-name-display">
            {fileName ? (
              <span className="file-name">{fileName}</span>
            ) : (
              <span className="file-placeholder">Pilih file foto...</span>
            )}
          </div>
        </div>
      </div>

      <div className="camera-preview-container">
        {isPreviewing ? (
          <div className="video-container">
            <video ref={videoRef} playsInline />
            <div className="action-buttons">
              <button onClick={capturePhoto} className="btn btn-primary">
                Ambil Foto
              </button>
              <button onClick={cancelPreview} className="btn btn-danger">
                Batal
              </button>
            </div>
          </div>
        ) : (
          <button onClick={startPreview} className="btn btn-primary">
            Buka Kamera
          </button>
        )}
      </div>

      {imagePreview && (
        <div className="preview-container">
          <h5 className="preview-title">Preview Foto:</h5>
          <img src={imagePreview} alt="Preview" className="responsive-image" />
          <div className="action-buttons">
            <button onClick={cancelPreview} className="btn btn-danger">
              Hapus Foto
            </button>
          </div>
        </div>
      )}

      <div className="action-buttons">
        <button 
          onClick={handleUpload} 
          className="btn btn-success" 
          disabled={!foto || !selectedQuestionnaire}
        >
          Upload Foto
        </button>
      </div>
    </div>
  );
};

export default UploadFoto;